import html from '../directMessageChat.html';
import { marked } from 'marked';

// Open links from message content in a new tab instead of replacing the app.
marked.use({
    renderer: {
        link({ href, title, tokens }) {
            const text = this.parser.parseInline(tokens);
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
        }
    }
});

export const renderingMixin = {

    renderMarkdown(content) {
        return marked.parse(content, { breaks: true });
    },

    highlightMentions(html) {
        if(!this.participantsCache) return html;

        for(const [userId, user] of this.participantsCache) {
            if(!user?.fullname || user.fullname === 'Not Found') continue;
            const escapedId = String(userId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`@${escapedId}(?![\\w])`, 'g');
            html = html.replace(regex, `<span class="mention" data-user-id="${userId}">@${user.fullname}</span>`);
        }
        return html;
    },

    //********************* RENDER ACTIVE CONVERSATION ********************************//
    // Renders the header + chat scaffold for the active view (dm or thread) and
    // replays its messages. Called whenever viewMode/dmState/threadState change.
    renderActiveConversation() {
        this.render(html);

        this.initEditor();

        this.renderAttachmentCards();

        if(this.viewMode === 'no_discussion') {
            this.renderNoDiscussionPlaceholder();
            return;
        }

        if(this.viewMode === 'dm' || this.viewMode === 'channel') {
            this.renderDiscussButton();
        }

        if(this.threadState?.kind === 'message') {
            const slot = this.querySelector('.thread_parent_message');
            if(slot) {
                slot.innerHTML = this.renderMessageHtml(this.threadState.parentMessage, { showThreadFooter: false });
                this.wireMessageActions(slot.firstElementChild, this.threadState.parentMessage);
            }
        }

        for(const message of this.activeMessages()) {
            this.addMessageToConversation(message);
        }

        this.scrollChatToBottom();

        if(this.viewMode === 'dm' || this.viewMode === 'channel') {
            this.renderItemDiscussionCards();
        }
    },

    //********************* RENDER DISCUSS BUTTON ********************************//
    // Shows/hides the "Discuss this item" button in the dm/channel header, based
    // on whether the user is currently on an open_item route. No-op outside
    // 'dm'/'channel' mode.
    renderDiscussButton() {
        const slot = this.querySelector('.dm_header__discuss_item_slot');
        if(!slot) return;

        if(!this.itemContext) {
            slot.innerHTML = '';
            return;
        }

        if(this.viewMode === 'dm' && !this._otherUserHasAppAccess) {
            slot.innerHTML = '';
            return;
        }

        const alreadyDiscussed = this.getSidebar()?.getDiscussedItems(this.getParticipantIds()).some(item =>
            item.item_ref?.app_id === this.itemContext.app_id && item.item_ref?.item_id === this.itemContext.item_id
        );

        slot.innerHTML = `<button type="button" class="discuss_item_button">${alreadyDiscussed ? 'Discuss this item' : 'Start discussion'}</button>`;
        slot.querySelector('button').onclick = () => this.openItemConversation();
    },

    //********************* RENDER NO DISCUSSION PLACEHOLDER ********************************//
    // Fills `.chat` for viewMode 'no_discussion' - the composer above it is
    // still fully functional, sendMessage() creates the item-thread on the
    // first send (see messagingMixin.js and conversationMixin.js's showNoDiscussion).
    renderNoDiscussionPlaceholder() {
        const chat = this.querySelector('.chat');
        if(!chat) return;

        chat.innerHTML = /*html*/`
            <div class="no_discussion_placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>
                <span>Start a discussion by typing a message below</span>
            </div>
        `;
    },

    //********************* RENDER MESSAGE HTML ********************************//
    // Builds the markup for a single message bubble, optionally including the
    // "Reply in thread" footer (dm and item-thread messages only - messages
    // inside a message-thread don't get nested threads, matching Slack).
    // * message (Object) — normalized message (see normalizeMessage)
    // * options.showThreadFooter (boolean) — include renderThreadFooter() output
    renderMessageHtml(message, { showThreadFooter }) {
        const isOwn = message.user_id === this.activeUserId;
        const user = this.resolveUser(message.user_id);
        const avatarUrl = user?.avatar_128 || user?.avatar_512;

        return /*html*/`
            <div class="message ${isOwn ? 'me' : ''}" data-timestamp="${message.timestamp}" data-message-id="${message.message_id}">
                <div class="user">
                    <gh-avatar-webcomponent name="${user?.fullname || 'Not Found'}"${avatarUrl ? ` url="${avatarUrl}"` : ''}></gh-avatar-webcomponent>
                </div>
                <div class="content">
                    <div class="message_toolbar">
                        <button type="button" class="message_toolbar__btn message_toolbar__react" title="Add reaction">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/></svg>
                        </button>
                        ${ showThreadFooter ? `
                        <button type="button" class="message_toolbar__btn message_toolbar__reply" title="Reply in thread">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                        </button>` : '' }
                    </div>
                    <div class="header">
                        <span class="name">${user?.fullname || 'Not Found'}</span>
                        <span class="time">
                            ${new Date(message.timestamp).toLocaleTimeString(navigator.language, {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </span>
                    </div>
                    <div class="message">
                        ${ !message.type ? this.highlightMentions(message.renderedContent) : '' }
                        ${ message.filename ? this.renderFileCardHtml(message) : '' }
                        ${ message.type === 'image' && !message.filename ? `<img src="${message.content}" alt="">` : ''}
                        ${ message.type === 'video' ? `<video src="${message.content}" controls></video>` : ''}
                        ${ message.type === 'audio' ? `<audio src="${message.content}" controls></audio>` : ''}
                        ${ message.type === 'file' && !message.filename ? `<a class="download_file" href="${message.content}" download>Download file</a>` : ''}
                    </div>
                    ${ this.renderReactionsHtml(message) }
                    ${ showThreadFooter ? this.renderThreadFooter(message) : '' }
                </div>
            </div>`;
    },

    //********************* RENDER FILE CARD HTML ********************************//
    // Dispatches to renderImageAttachmentHtml for image/* files (Slack-style
    // inline image with hover toolbar) or renderGenericFileCardHtml for all
    // other types (compact card with icon, name, size, download, delete).
    // The delete button is wired up by wireMessageActions().
    // * message (Object) — normalized message (see normalizeMessage)
    renderFileCardHtml(message) {
        if(message.mime_type?.startsWith('image/')) {
            return this.renderImageAttachmentHtml(message);
        }
        return this.renderGenericFileCardHtml(message);
    },

    //********************* RENDER IMAGE ATTACHMENT HTML ********************************//
    // Slack-style: filename caption above the image, full-width image, and a
    // controls toolbar (download + delete) that fades in on message hover.
    // * message (Object) — normalized message
    renderImageAttachmentHtml(message) {
        const canDelete = message.user_id === this.activeUserId && message.filename;

        return `<div class="file_image">
            <span class="file_image__name">${message.file_name || 'Image'}</span>
            <div class="file_image__wrapper">
                <img class="file_image__img" src="${message.thumbnail_url || message.content}" alt="${message.file_name || ''}">
                <div class="file_image__toolbar">
                    <a class="file_image__btn" href="${message.content}" download="${message.file_name || ''}" title="Download">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                    </a>
                    ${canDelete ? `
                    <button class="file_image__btn file_image__btn--delete file_card__delete" type="button" title="Delete">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                    </button>` : ''}
                </div>
            </div>
        </div>`;
    },

    //********************* GET FILE TYPE INFO ********************************//
    // Maps MIME type / extension to a display label and brand colour for the
    // file card badge. Returns null for unrecognised types (shows generic icon).
    // * mimeType (string|null)
    // * fileName (string|null)
    getFileTypeInfo(mimeType, fileName) {
        const ext = (fileName || '').split('.').pop().toLowerCase();
        const types = [
            { label: 'PDF',  color: '#e5252a', mimes: ['application/pdf'],                                                                                                                                                 exts: ['pdf'] },
            { label: 'DOC',  color: '#2b579a', mimes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],                                                                    exts: ['doc', 'docx'] },
            { label: 'XLS',  color: '#217346', mimes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],                                                                exts: ['xls', 'xlsx'] },
            { label: 'CSV',  color: '#00897b', mimes: ['text/csv'],                                                                                                                                                        exts: ['csv'] },
            { label: 'PPT',  color: '#d24726', mimes: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],                                                      exts: ['ppt', 'pptx'] },
            { label: 'ZIP',  color: '#e8a020', mimes: ['application/zip', 'application/x-zip-compressed', 'application/x-rar-compressed', 'application/x-7z-compressed', 'application/gzip', 'application/x-tar'],       exts: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'] },
            { label: 'PSD',  color: '#31a8ff', mimes: ['image/vnd.adobe.photoshop', 'image/x-photoshop'],                                                                                                                  exts: ['psd'] },
            { label: 'AI',   color: '#ff7c00', mimes: ['application/postscript', 'application/illustrator'],                                                                                                               exts: ['ai', 'eps'] },
            { label: 'MP4',  color: '#c62828', mimes: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm'],                                                                          exts: ['mp4', 'mov', 'avi', 'mkv', 'webm'] },
            { label: 'WAV',  color: '#6a1b9a', mimes: ['audio/wav', 'audio/mpeg', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/x-m4a'],                                                                                 exts: ['wav', 'mp3', 'ogg', 'flac', 'aac', 'm4a'] },
            { label: 'TXT',  color: '#767676', mimes: ['text/plain'],                                                                                                                                                      exts: ['txt', 'md', 'log'] },
            { label: 'CODE', color: '#3d9e4a', mimes: ['application/json', 'application/javascript', 'text/javascript', 'text/html', 'text/css', 'application/xml', 'text/xml'],                                         exts: ['js', 'ts', 'jsx', 'tsx', 'json', 'html', 'css', 'scss', 'py', 'java', 'cpp', 'c', 'go', 'rs', 'php', 'rb', 'swift', 'kt'] },
        ];
        return types.find(t => t.mimes.includes(mimeType) || t.exts.includes(ext)) || null;
    },

    //********************* RENDER GENERIC FILE CARD HTML ********************************//
    // Compact card for non-image attachments: icon/thumbnail, filename, size,
    // download link, and (for own messages) a delete button.
    // * message (Object) — normalized message
    renderGenericFileCardHtml(message) {
        const deleteBtn = message.user_id === this.activeUserId && message.filename
            ? `<button class="file_card__delete" type="button" title="Delete">
                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
               </button>`
            : '';

        const thumbnailSection = message.thumbnail_url
            ? `<div class="file_card__thumbnail">
                   <img src="${message.thumbnail_url}" alt="${message.file_name || ''}">
               </div>`
            : '';

        const typeInfo = this.getFileTypeInfo(message.mime_type, message.file_name);
        const isPdf = message.mime_type === 'application/pdf';

        const pdfSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24"><path d="M337.4,267.3c-14.8-11.6-28.8-23.6-38.2-33c-12.2-12.2-23-24-32.4-35.2c14.7-45.4,21.1-68.8,21.1-81.3c0-53-19.2-63.9-47.9-63.9c-21.9,0-47.9,11.4-47.9,65.4c0,23.8,13.1,52.8,38.9,86.4c-6.3,19.3-13.8,41.6-22.1,66.7c-4,12.1-8.4,23.2-13,33.6c-3.8,1.7-7.4,3.4-10.9,5.1c-12.7,6.3-24.8,12.1-35.9,17.4c-51,24.1-84.7,40.1-84.7,71.7c0,22.9,24.9,37.1,47.9,37.1c29.7,0,74.5-39.7,107.3-106.5c34-13.4,76.3-23.4,109.6-29.6c26.7,20.6,56.3,40.2,70.6,40.2c39.6,0,47.9-22.9,47.9-42.1c0-37.8-43.1-37.8-63.9-37.8C377.3,261.6,360.1,263.5,337.4,267.3z M112.3,405.4c-9.1,0-15.3-4.3-16-5.1c0-11.3,33.8-27.3,66.4-42.8c2.1-1,4.2-2,6.3-3C145,389.3,121.3,405.4,112.3,405.4z M224.1,119.4c0-33.5,10.4-33.5,16-33.5c11.3,0,16,0,16,31.9c0,6.7-4.5,23.6-12.7,49.9C230.7,148.5,224.1,131.9,224.1,119.4z M236.3,291.1c1-2.8,2-5.6,2.9-8.4c5.9-17.8,11.3-33.8,16-48.1c6.6,7.3,13.8,14.8,21.4,22.4c3,3,10.4,9.7,20.3,18.2C277.3,279.4,256.3,284.7,236.3,291.1z M415.7,299.4c0,7.2,0,10.2-14.8,10.2c-4.4-0.9-14.4-6.9-26.8-15.3c4.5-0.5,7.8-0.7,9.7-0.7C407.4,293.6,414.1,295.9,415.7,299.4z"/></svg>`;

        let previewStyle = '';
        let previewInner;

        if (isPdf) {
            previewStyle = `style="background:#e5252a"`;
            previewInner = pdfSvg;
        } else if (typeInfo) {
            previewStyle = `style="background:${typeInfo.color}"`;
            previewInner = `<span class="file_card__type_badge">${typeInfo.label}</span>`;
        } else {
            previewInner = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>`;
        }

        return `<div class="file_card">
            <div class="file_card__header">
                <div class="file_card__preview" ${previewStyle}>
                    ${previewInner}
                </div>
                <div class="file_card__info">
                    <span class="file_card__name" title="${message.file_name || ''}">${message.file_name || 'File'}</span>
                    ${message.file_size != null ? `<span class="file_card__size">${this.formatFileSize(message.file_size)}</span>` : ''}
                </div>
                <div class="file_card__actions">
                    <a class="file_card__download" href="${message.content}" download="${message.file_name || ''}" title="Download">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                    </a>
                    ${deleteBtn}
                </div>
            </div>
            ${thumbnailSection}
        </div>`;
    },

    //********************* RENDER THREAD FOOTER ********************************//
    // Slack-style "N replies · View thread" row shown under dm and item-thread
    // messages once a thread exists off this message (started via the
    // toolbar's "Reply in thread" button - see wireMessageActions). Shows the
    // avatars of the users who replied (see normalizeMessage's
    // thread_participants). Opens the message-thread view.
    // * message (Object) — normalized message (see normalizeMessage)
    renderThreadFooter(message) {
        if(!message.thread_id) return '';

        const avatars = message.thread_participants.slice(0, 5).map(userId => {
            const user = this.resolveUser(userId);
            const avatarUrl = user?.avatar_128 || user?.avatar_512;
            return `<gh-avatar-webcomponent name="${user?.fullname || 'Not Found'}"${avatarUrl ? ` url="${avatarUrl}"` : ''}></gh-avatar-webcomponent>`;
        }).join('');

        return /*html*/`
            <button type="button" class="message__thread_footer">
                ${ avatars ? `<span class="message__thread_footer__avatars">${avatars}</span>` : '' }
                <span class="message__thread_footer__count">${message.thread_reply_count} repl${message.thread_reply_count === 1 ? 'y' : 'ies'}</span>
                <span class="message__thread_footer__label">View thread</span>
                <svg class="message__thread_footer__arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>
            </button>`;
    },

    //********************* RENDER REACTIONS HTML ********************************//
    // Reaction pills shown under a message's content - one per distinct emoji,
    // with its reaction count, highlighted if the active user is among the
    // reactors. Wired up by wireReactionPills().
    // * message (Object) — { reactions } (see normalizeMessage)
    renderReactionsHtml(message) {
        if(!message.reactions?.length) return '';

        return `<div class="message_reactions">
            ${message.reactions.map(reaction => `
                <button type="button" class="message_reaction${reaction.user_ids.includes(this.activeUserId) ? ' message_reaction--active' : ''}" data-emoji="${reaction.emoji}">
                    <span class="message_reaction__emoji">${reaction.emoji}</span>
                    <span class="message_reaction__count">${reaction.user_ids.length}</span>
                </button>
            `).join('')}
        </div>`;
    },

    //********************* ADD MESSAGE TO CONVERSATION ********************************//
    // Appends a single normalized message to the chat, including a date
    // separator when it's the first message of a new day.
    // * message (Object) — normalized message (see normalizeMessage)
    addMessageToConversation(message) {
        const messages = this.activeMessages();
        const index = messages.indexOf(message);
        const previous = messages[index - 1];

        const dateSeparator = !previous || new Date(message.timestamp).getDate() !== new Date(previous.timestamp).getDate() ? `
            <div class="date"><span>${new Date(message.timestamp).toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
            })}</span></div>
        ` : '';

        const showThreadFooter = this.viewMode === 'dm' || this.viewMode === 'channel' || (this.viewMode === 'thread' && this.threadState?.kind === 'item');

        const chat = this.querySelector('.chat');
        chat.insertAdjacentHTML("beforeend", dateSeparator + this.renderMessageHtml(message, { showThreadFooter }));

        this.wireMessageActions(chat.lastElementChild, message);

        if(this.threadState?.kind === 'message') {
            const divider = this.querySelector('.thread_divider');
            if(divider) divider.textContent = `${messages.length} repl${messages.length === 1 ? 'y' : 'ies'}`;
        }
    },

    scrollChatToBottom() {
        const chat = this.querySelector('.chat');
        if(chat) chat.scrollTop = chat.scrollHeight;
    },

    //********************* RENDER HEADER SUBTITLE ********************************//
    // Refreshes the channel/item-thread header's member-count subtitle after
    // addPeople() changes the conversation's participants.
    // * participants (Array) — updated participants list
    // * label (string) — 'member' (channel) or 'participant' (item-thread)
    renderHeaderSubtitle(participants, label) {
        const subtitle = this.querySelector('.dm_header__subtitle');
        if(!subtitle) return;

        const count = participants.length;
        subtitle.textContent = `${count} ${label}${count === 1 ? '' : 's'}`;
    },
};
